import { useEffect, useState } from "react";
import { Toaster, toast } from "sonner";
import { apiFetch } from "../lib/api";

const inputCls = "w-full border border-ink/20 bg-white px-3 py-2 text-sm outline-none focus:border-amber";

const STATUS_COLOR = {
  CONNECTED: "bg-emerald-100 text-emerald-800",
  "READY TO CONNECT": "bg-amber/15 text-amber",
  "ACTION REQUIRED": "bg-red-100 text-red-700",
  ERROR: "bg-red-100 text-red-700",
};

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });

  useEffect(() => {
    apiFetch("/admin/settings").then(setSettings).catch(() => {});
  }, []);

  if (!settings) return <p className="text-sm text-ink/50">Loading…</p>;

  const set = (key) => (e) => setSettings({ ...settings, [key]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    await apiFetch("/admin/settings", {
      method: "PUT",
      body: JSON.stringify({
        hotline_display: settings.hotline_display,
        whatsapp_number: settings.whatsapp_number,
        email: settings.email,
        clinic_name: settings.clinic_name,
        clinic_address: settings.clinic_address,
        opening_hours: settings.opening_hours,
        opening_hours_published: settings.opening_hours_published,
        ga4_id: settings.ga4_id,
        google_ads_id: settings.google_ads_id,
      }),
    });
    toast.success("Settings saved");
  };

  const changePassword = async (e) => {
    e.preventDefault();
    try {
      await apiFetch("/auth/password", { method: "PUT", body: JSON.stringify(pw) });
      toast.success("Password updated");
      setPw({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div data-testid="admin-settings">
      <Toaster position="top-center" richColors />
      <p className="eyebrow">Settings</p>
      <h1 className="mt-2 font-serif text-3xl tracking-tight text-ink">Central settings</h1>

      <form onSubmit={save} className="mt-8 grid gap-4 border border-ink/10 bg-warmwhite p-6 sm:grid-cols-2" data-testid="settings-form">
        <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50 sm:col-span-2">Contact & clinic</p>
        <label className="block">
          <span className="mb-1 block text-xs text-ink/60">Hotline (display)</span>
          <input value={settings.hotline_display || ""} onChange={set("hotline_display")} className={inputCls} data-testid="settings-hotline" />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs text-ink/60">WhatsApp number (international, no +)</span>
          <input value={settings.whatsapp_number || ""} onChange={set("whatsapp_number")} className={inputCls} data-testid="settings-whatsapp" />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs text-ink/60">Email</span>
          <input value={settings.email || ""} onChange={set("email")} className={inputCls} data-testid="settings-email" />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs text-ink/60">Clinic name</span>
          <input value={settings.clinic_name || ""} onChange={set("clinic_name")} className={inputCls} data-testid="settings-clinic-name" />
        </label>
        <label className="block sm:col-span-2">
          <span className="mb-1 block text-xs text-ink/60">Clinic address</span>
          <input value={settings.clinic_address || ""} onChange={set("clinic_address")} className={inputCls} data-testid="settings-clinic-address" />
        </label>
        <label className="block sm:col-span-2">
          <span className="mb-1 block text-xs text-ink/60">Opening hours (not published until confirmed)</span>
          <input value={settings.opening_hours || ""} onChange={set("opening_hours")} className={inputCls} data-testid="settings-hours" placeholder="e.g. Mon–Sat, 9am–6pm" />
        </label>
        <label className="flex items-center gap-2 text-xs text-ink/70 sm:col-span-2">
          <input type="checkbox" checked={!!settings.opening_hours_published} onChange={(e) => setSettings({ ...settings, opening_hours_published: e.target.checked })} data-testid="settings-hours-published" />
          Publish opening hours on the Dental site
        </label>
        <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50 sm:col-span-2">Analytics & ads (Ready to Connect)</p>
        <label className="block">
          <span className="mb-1 block text-xs text-ink/60">GA4 Measurement ID</span>
          <input value={settings.ga4_id || ""} onChange={set("ga4_id")} className={inputCls} data-testid="settings-ga4" placeholder="G-XXXXXXXXXX" />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs text-ink/60">Google Ads ID</span>
          <input value={settings.google_ads_id || ""} onChange={set("google_ads_id")} className={inputCls} data-testid="settings-ads" placeholder="AW-XXXXXXXXX" />
        </label>
        <button type="submit" className="btn-primary !px-5 !py-2.5 sm:col-span-2 sm:justify-self-start" data-testid="settings-save">
          Save Settings
        </button>
      </form>

      <section className="mt-10 border border-ink/10 bg-warmwhite p-6" data-testid="settings-integrations">
        <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50">Integration status</p>
        <ul className="mt-5 space-y-3">
          {Object.entries(settings.integrations || {}).map(([key, intg]) => (
            <li key={key} className="flex items-center justify-between gap-4 text-sm">
              <span className="text-ink/75">{intg.label}</span>
              <span className={`px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.15em] ${STATUS_COLOR[intg.status] || "bg-softgrey text-ink/60"}`} data-testid={`settings-integration-${key}`}>
                {intg.status}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <form onSubmit={changePassword} className="mt-10 grid max-w-md gap-4 border border-ink/10 bg-warmwhite p-6" data-testid="password-form">
        <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50">Change admin password</p>
        <input type="password" required placeholder="Current password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className={inputCls} data-testid="password-current" />
        <input type="password" required minLength={10} placeholder="New password (min 10 characters)" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className={inputCls} data-testid="password-new" />
        <button type="submit" className="btn-primary !px-5 !py-2.5 justify-self-start" data-testid="password-submit">
          Update Password
        </button>
      </form>
    </div>
  );
}
